// Explicit sport revisions of the day engines (design sections 2.2, 2.9 and 8.5). Pure data
// with no kernel imports, so lib/conditions/sport-scoring-versions.ts can validate
// CONDITIONS_SPORT_REVISIONS against it without importing any engine (registry.ts adds the
// kernels and their availability).
//
// Every non-ski sport declares:
//   1  the legacy revision: today's stored truth, produced by the legacy code and wrapped by
//      the family's revision 1 kernel (WP1 to WP3); its registration fingerprint is the seeded
//      `legacy:<sport>:1`.
//   2  the revision 2 kernel in engines/<family>/r2/<sport>.ts (a stub exporting null until
//      its WP-S package lands).
//   3  the rollback alias of revision 1 (design 8.5): the same formula under the next number,
//      so a rollback after a revision 2 flip always moves upwards past the whole-row guard.
//      If a revision 2 is abandoned in shadow and its replacement formula takes number 3, the
//      alias moves to the next free number here in the same change.
import type { EngineFamily, EngineSport } from "./types";
import { ENGINE_SPORTS } from "./sports";

export type RevisionDeclaration =
  | Readonly<{ kind: "legacy"; legacyEquivalent: `legacy:${EngineSport}:1` }>
  | Readonly<{ kind: "kernel" }>
  | Readonly<{ kind: "alias"; aliasOf: number }>;

export const LEGACY_REVISION = 1;
export const REVISION_TWO = 2;
export const ROLLBACK_ALIAS_REVISION = 3;
/** Highest revision number score_revisions accepts (smallint). */
export const MAX_REVISION = 32767;

/**
 * Usable-input floor for legacy stored hours (XS-4, Track A): daily_observations rows below
 * this coordinated scoring_version never feed an engine or the legacy copy. Equals
 * PRE_SUP_PROFILE_DAILY_VERSION in lib/conditions/scoring-versions.ts (a test pins it) and
 * the `scoring_version >= 11` filter of copy_legacy_scores_to_score_day.
 */
export const LEGACY_HOURS_FLOOR = 11;

/**
 * The database legacy_floor per family (private.sport_scoring_definition(): 18 for coastal and
 * sailing sports, 19 for MTB). At or above it an unversioned stored score is revision 1
 * (storedSportScoringVersion). Rows between LEGACY_HOURS_FLOOR and this floor feed revision 2
 * replays as inputs; whether they also yield revision 1 facts follows the legacy copy, which
 * floors at LEGACY_HOURS_FLOOR only (see r1-fact.ts and the WP0 implementation notes).
 */
export const REVISION_ONE_LEGACY_FLOOR: Readonly<Record<EngineFamily, number>> = Object.freeze({
  coastal: 18,
  sailing: 18,
  mtb: 19,
});

function declarationsFor(sport: EngineSport): Readonly<Record<number, RevisionDeclaration>> {
  return Object.freeze({
    [LEGACY_REVISION]: Object.freeze({ kind: "legacy", legacyEquivalent: `legacy:${sport}:1` as const }),
    [REVISION_TWO]: Object.freeze({ kind: "kernel" }),
    [ROLLBACK_ALIAS_REVISION]: Object.freeze({ kind: "alias", aliasOf: LEGACY_REVISION }),
  });
}

export const SPORT_REVISIONS: Readonly<Record<EngineSport, Readonly<Record<number, RevisionDeclaration>>>> =
  Object.freeze(Object.fromEntries(ENGINE_SPORTS.map((sport) => [sport, declarationsFor(sport)])) as Record<
    EngineSport,
    Readonly<Record<number, RevisionDeclaration>>
  >);

export function isValidRevisionNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 1 && value <= MAX_REVISION;
}

export function revisionDeclaration(sport: EngineSport, revision: number): RevisionDeclaration | null {
  if (!isValidRevisionNumber(revision)) return null;
  const declarations = SPORT_REVISIONS[sport];
  return declarations && Object.hasOwn(declarations, revision) ? declarations[revision] : null;
}

export function isRevisionDeclared(sport: EngineSport, revision: number): boolean {
  return revisionDeclaration(sport, revision) !== null;
}

/** Declared revisions of a sport, ascending. */
export function declaredRevisions(sport: EngineSport): number[] {
  return Object.keys(SPORT_REVISIONS[sport]).map(Number).sort((a, b) => a - b);
}

/**
 * True for a rollback alias of revision 1 (design 8.5): a declared revision other than 1 whose
 * formula is revision 1's. Since owner decision D6 (6 October 2026) such a revision is also the
 * consistency-switch target, today's formula rebuilt on the input frames, so its facts are the
 * alias kernel's result on the day's frames input (core/r1-fact.ts revisionOneAliasFact), never a
 * copy of the stored revision 1 score.
 */
export function isRevisionOneAlias(sport: EngineSport, revision: number): boolean {
  return revision !== LEGACY_REVISION && formulaRevision(sport, revision) === LEGACY_REVISION;
}

/**
 * The revision whose formula `revision` runs: itself, or the end of its alias chain. Null for
 * an undeclared revision, a dangling alias or an alias cycle.
 */
export function formulaRevision(sport: EngineSport, revision: number): number | null {
  const seen = new Set<number>();
  let current = revision;
  for (;;) {
    const declaration = revisionDeclaration(sport, current);
    if (!declaration || seen.has(current)) return null;
    if (declaration.kind !== "alias") return current;
    seen.add(current);
    current = declaration.aliasOf;
  }
}
